import React, { useMemo } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'

import {
  Route,
  Routes,
  useLocation,
  useMatch,
  useParams,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import { SSRNavigate } from '@newComponents/SSRNavigate'
import { UserMetadata } from '@newComponents/User/UserMetadata'
import { wrapSuspenseScreen } from '@newComponents/SuspenseWrapper'
import { usePaletteEffect } from '@newComponents/Palette'
import { UserCard } from '@newComponents/User/UserCard'

import { Text } from '@newComponents/Text'
import { Icon } from '@newComponents/Icon'
import { Screen } from '@newComponents/Screen'
import { Grid } from '@newComponents/Grid'
import { Group } from '@newComponents/Group'
import { Link } from '@newComponents/Link'

import { YStack, XStack } from 'tamagui'

import { MobileNavDropdown } from '@newComponents/MobileNavDropdown'

import {
  UserNavigatorQuery,
  UserNavigatorQuery$data,
} from './__generated__/UserNavigatorQuery.graphql'

const UserEventsScreen = wrapSuspenseScreen(
  loadable(() => import('../screens/UserEventsScreen/UserEventsScreen'))
)

const UserPresentationsScreen = wrapSuspenseScreen(
  loadable(
    () => import('../screens/UserPresentationsScreen/UserPresentationsScreen')
  )
)

const UserGuildMembershipsScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import('../screens/UserGuildMembershipsScreen/UserGuildMembershipsScreen')
  )
)

const UserBentoScreen = wrapSuspenseScreen(
  loadable(() => import('../screens/UserBentoScreen/UserBentoScreen'))
)

const UserEditNavigator = wrapSuspenseScreen(
  loadable(() => import('./UserEditNavigator'))
)

export const UserNavigator = () => {
  const params = useParams()

  const data = useLazyLoadQuery<UserNavigatorQuery>(
    graphql`
      query UserNavigatorQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on User {
            primaryPhoto {
              colorPalette
            }

            ...UserCardNew_user
            ...UserMetadata_user

            totalEventAttendance: eventAttendance {
              totalCount
            }

            totalPresentations: presentationsByPresenterId {
              totalCount
            }
          }
        }
      }
    `,
    { id: params.actorId || '' }
  )

  usePaletteEffect(
    data.nodeBySlugId?.__typename === 'User'
      ? data.nodeBySlugId.primaryPhoto?.colorPalette
      : null
  )

  const initialRoute = useMemo(() => {
    // This should never actually be rendered, but needed to appease TypeScript
    if (data.nodeBySlugId?.__typename !== 'User') {
      return 'guilds'
    }

    if (data.nodeBySlugId.totalPresentations.totalCount > 0) {
      return 'presentations'
    }

    if (data.nodeBySlugId.totalEventAttendance.totalCount > 0) {
      return 'events'
    }

    return 'guilds'
  }, [data.nodeBySlugId])

  const location = useLocation()

  if (!data.nodeBySlugId || data.nodeBySlugId.__typename !== 'User') {
    // TODO
    console.error('No User found', params.actorId)
    return null
  }

  const locationState = location.state as { backgroundLocation?: Location }

  return (
    <>
      <UserMetadata user={data.nodeBySlugId} />
      <Routes location={locationState?.backgroundLocation || location}>
        <Route path='settings/*' element={<UserEditNavigator />} />

        <Route
          path='*'
          element={
            <UserNavigatorSidebar data={data}>
              <Routes location={locationState?.backgroundLocation || location}>
                <Route
                  path='presentations'
                  element={<UserPresentationsScreen />}
                />
                <Route path='events/*' element={<UserEventsScreen />} />
                <Route path='guilds' element={<UserGuildMembershipsScreen />} />

                <Route index element={<UserBentoScreen />} />
                <Route
                  path='*'
                  element={<SSRNavigate to={initialRoute} replace />}
                />
              </Routes>
            </UserNavigatorSidebar>
          }
        />
      </Routes>
    </>
  )
}

const UserNavigatorCardSection = ({
  data,
}: {
  data: UserNavigatorQuery$data
}) => {
  const params = useParams()

  // useMatch matches against location data which encodes any unicode slugs
  const actorIdMatch = encodeURIComponent(params.actorId || '')
  const homeMatch = useMatch(`/${actorIdMatch}`)
  const presentationsMatch = useMatch(`/${actorIdMatch}/presentations`)
  const eventsMatch = useMatch(`/${actorIdMatch}/events/*`)
  const guildsMatch = useMatch(`/${actorIdMatch}/guilds`)

  const selectedIndex = useMemo<number>(() => {
    if (data.nodeBySlugId?.__typename !== 'User') {
      return 0
    }

    const hasPresentations = data.nodeBySlugId.totalPresentations.totalCount > 0
    const hasEvents = data.nodeBySlugId.totalEventAttendance.totalCount > 0

    // Home is always index 0
    if (homeMatch) {
      return 0
    }

    // Remaining tabs start at index 1
    let index = 1

    if (presentationsMatch) {
      return index
    }
    if (hasPresentations) index++

    if (eventsMatch) {
      return index
    }
    if (hasEvents) index++

    if (guildsMatch) {
      return index
    }

    return -1
  }, [data, homeMatch, presentationsMatch, eventsMatch, guildsMatch])

  const navLabel = useMemo(() => {
    if (homeMatch) return 'User'
    if (presentationsMatch) return 'Presentations'
    if (eventsMatch) return 'Events'
    if (guildsMatch) return 'Guilds'
    return 'User'
  }, [homeMatch, presentationsMatch, eventsMatch, guildsMatch])

  if (data.nodeBySlugId?.__typename !== 'User') {
    return null
  }

  return (
    <YStack gap='$4' $gtXs={{ gap: '$8' }}>
      <UserCard user={data.nodeBySlugId} />

      <MobileNavDropdown
        label={navLabel}
        openLabel='Navigation'
        icon={<Icon name='navigation' color='$color' />}
        $gtXs={{ display: 'none' }}
      >
        <UserNavigation data={data} activeIndex={selectedIndex} />
      </MobileNavDropdown>

      <UserNavigation
        data={data}
        activeIndex={selectedIndex}
        display='none'
        $gtXs={{ display: 'flex' }}
      />
    </YStack>
  )
}

const UserNavigatorSidebar = ({
  children,
  data,
}: {
  data: UserNavigatorQuery$data
  children: React.ReactNode
}) => {
  if (data.nodeBySlugId?.__typename !== 'User') {
    return null
  }

  return (
    <Screen>
      <YStack gap='$8' flex={1} $gtXs={{ display: 'none' }}>
        <UserNavigatorCardSection data={data} />

        {children}
      </YStack>

      <Grid display='none' $gtXs={{ display: 'flex' }} gap='$8'>
        <Grid.Column flex={5} $gtSm={{ flex: 4 }} $gtMd={{ flex: 3 }}>
          <YStack gap='$4' style={{ position: 'sticky', top: 84 }}>
            <UserNavigatorCardSection data={data} />
          </YStack>
        </Grid.Column>

        <Grid.Column flex={5} $gtSm={{ flex: 8 }} $gtMd={{ flex: 9 }}>
          {children}
        </Grid.Column>
      </Grid>
    </Screen>
  )
}

const UserNavigation = Group.styleable<{
  data: UserNavigatorQuery$data
}>(({ data, ...props }, ref) => {
  const params = useParams()

  if (data.nodeBySlugId?.__typename !== 'User') {
    return null
  }

  let currentIndex = 0

  return (
    <Group
      {...props}
      ref={ref}
      gap='$2'
      highlightedElementProps={{ borderRadius: '$6' }}
    >
      <Group.Item index={currentIndex++}>
        <Link
          href={`/${params.actorId}`}
          hoverStyle={{ textDecorationLine: 'none' }}
          paddingHorizontal='$5'
          paddingVertical='$2'
        >
          <XStack alignItems='center' gap='$3'>
            <Icon name='home' color='$color' />

            <Text color='$color' fontWeight='$5'>
              User
            </Text>
          </XStack>
        </Link>
      </Group.Item>

      {data.nodeBySlugId.totalPresentations.totalCount > 0 && (
        <Group.Item index={currentIndex++}>
          <Link
            href={`/${params.actorId}/presentations`}
            hoverStyle={{ textDecorationLine: 'none' }}
            paddingHorizontal='$5'
            paddingVertical='$2'
          >
            <XStack alignItems='center' gap='$3'>
              <Icon name='projection-screen' color='$color' />

              <Text color='$color' fontWeight='$5'>
                Presentations
              </Text>
            </XStack>
          </Link>
        </Group.Item>
      )}

      {data.nodeBySlugId.totalEventAttendance.totalCount > 0 && (
        <Group.Item index={currentIndex++}>
          <Link
            href={`/${params.actorId}/events`}
            hoverStyle={{ textDecorationLine: 'none' }}
            paddingHorizontal='$5'
            paddingVertical='$2'
          >
            <XStack alignItems='center' gap='$3'>
              <Icon name='calendar' color='$color' />

              <Text color='$color' fontWeight='$5'>
                Events
              </Text>
            </XStack>
          </Link>
        </Group.Item>
      )}

      <Group.Item index={currentIndex++}>
        <Link
          href={`/${params.actorId}/guilds`}
          hoverStyle={{ textDecorationLine: 'none' }}
          paddingHorizontal='$5'
          paddingVertical='$2'
        >
          <XStack alignItems='center' gap='$3'>
            <Icon name='people' color='$color' />

            <Text color='$color' fontWeight='$5'>
              Guilds
            </Text>
          </XStack>
        </Link>
      </Group.Item>
    </Group>
  )
})

export default UserNavigator
